import cx from 'classnames';
import _ from 'lodash';
import { t } from 'i18next';
import { Trans } from 'react-i18next';
import { Fragment, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import ReactSelect, { ActionMeta, components as ReactSelectComponents, SelectInstance } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import AsyncSelect from 'react-select/async';
import { RioSelectProvider, useRioSelectContext } from './context';
import { IRioSelectProps, TRioSelectViewConfig } from './types';
import {
  extractTextFromReactNode,
  getOptionFromReactSelectOption,
  getValueFromReactSelectOption,
  identity,
  isGroup,
  normalizeOption,
  sortOptions,
  useCustomReactMenuTrigger
} from './utils';
import { useIsTouchDevice } from '../../grid/hooks';
import { useFormContext } from '../../form/Form';
import { FormControlInput } from '../../form/FormControlInput';
import { Stack } from '../../stack/Stack';
import { eventHandlerUtils } from '../../../libs/utils';
import { InlineSpinner } from '../spinner';

const SHOW_SEARCH_IN_MENU_IF_TOUCH_DEVICE = false;

const DEFAULT_VIEW_CONFIG: TRioSelectViewConfig = {
  hideDropdownIndicator: false,
  hideBorderBottom: false
};

const DEFAULT_LOAD_OPTIONS_DEBOUNCE_DELAY = 500;

export function RioSelect<Option, Value, IsMulti extends boolean = false, IsClearable extends boolean = false>(
  props: IRioSelectProps<Option, Value, IsMulti, IsClearable>
) {
  const instanceId = useId();
  const isTouchDevice = useIsTouchDevice();
  const {
    size: formControlSize = 'medium',
    theme: formTheme,
    showModifiedIndicator: formShowModifiedIndicator
  } = useFormContext() || {};

  const {
    options,
    value,
    onChange: onChangeProp,
    isClearable = false,
    isSearchable = true,
    search,
    onSearchChange,
    placeholder,
    disabled,
    formatOptionLabel,
    getOptionLabel = identity,
    getOptionValue = identity,
    isMulti = false,
    closeMenuOnSelect = !isMulti,
    isLoading,
    className,
    creatable,
    autoFocus,
    theme = formTheme !== undefined ? formTheme : 'dark',
    size = formControlSize === 'medium' ? 'medium' : 'small',
    styles,
    noOptionsMessage,
    isOptionDisabled,
    menuIsOpen: menuIsOpenProp,
    onMenuOpen,
    onMenuClose,
    filterOption,
    formatGroupLabel,
    components,
    renderPlaceholderAsValue: propsRenderPlaceholderAsValue,
    emptyOption,
    allowSelectSameOption = false,
    hideSelectedOptions = allowSelectSameOption ? false : undefined,
    variant = 'default',
    valueColor = 'default',
    displaySearchInMenu = SHOW_SEARCH_IN_MENU_IF_TOUCH_DEVICE ? isTouchDevice : false,
    sortFunction,
    valuePrefix,
    isOptionRemovable,
    selectRef: selectRefProp,
    useCustomMenuTrigger,
    shouldTriggerOnEqualOptionChange = true,
    viewConfig,
    loadOptions,
    minSearchLengthToLoadOptions = 1,
    loadOptionsDebounceDelay = DEFAULT_LOAD_OPTIONS_DEBOUNCE_DELAY,
    prefix,
    suffix,
    onFocus,
    onBlur,
    cacheOptions = true,
    optionsLoadingMessage,
    menuPortalTarget,
    menuPosition,
    ariaLabel
  } = props;

  const { hideDropdownIndicator, hideBorderBottom } = { ...DEFAULT_VIEW_CONFIG, ...viewConfig };

  const ref = useRef<SelectInstance | null>(null);

  const isSearchMenuEnabled = displaySearchInMenu && !!isSearchable;

  const allCurrentOptions = useMemo(
    () => _.flatten(options?.map((option) => (isGroup(option) ? option.options : [option])) || []),
    [options]
  );

  const [savedOptions, setSavedOptions] = useState<Option[]>(allCurrentOptions);

  const mergeSavedOptions = useCallback(
    (current: Option[], incoming: Option[]) => {
      const incomingByValue = new Map(incoming.map((option) => [getOptionValue(option), option]));

      let hasChanged = false;
      const next: Option[] = current.map((option) => {
        const updated = incomingByValue.get(getOptionValue(option));
        if (updated !== undefined && updated !== option && !_.isEqual(updated, option)) {
          hasChanged = true;
          return updated;
        }
        return option;
      });

      const currentKeys = new Set(current.map((option) => getOptionValue(option)));
      for (const option of incoming) {
        if (!currentKeys.has(getOptionValue(option))) {
          next.push(option);
          hasChanged = true;
        }
      }

      return hasChanged ? next : current;
    },
    [getOptionValue]
  );

  useEffect(() => {
    setSavedOptions((current) => mergeSavedOptions(current, allCurrentOptions));
  }, [allCurrentOptions, mergeSavedOptions]);

  const loadOptionsTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (loadOptionsTimeoutRef.current) {
        clearTimeout(loadOptionsTimeoutRef.current);
      }
    };
  }, []);

  const isCustomMenuTriggers = !!isSearchMenuEnabled || !!useCustomMenuTrigger;

  const [menuIsOpen, setMenuIsOpen] = useCustomReactMenuTrigger({
    selectRef: ref,
    initialMenuIsOpen: menuIsOpenProp,
    closeMenuOnSelect,
    disabled: !isCustomMenuTriggers
  });

  const [inputValue, setInputValue] = useState(search);

  useEffect(() => {
    setInputValue(search);
  }, [search]);

  useEffect(() => {
    if (menuIsOpenProp !== undefined) {
      setMenuIsOpen(menuIsOpenProp);
    }
  }, [menuIsOpenProp]);

  useEffect(() => {
    if (!menuIsOpen) {
      setInputValue('');
      onSearchChange?.('');
    }
  }, [menuIsOpen]);

  const selected = useMemo(() => {
    if (isMulti) {
      if (allowSelectSameOption) {
        if (!Array.isArray(value)) {
          return [] as any;
        }

        return value.flatMap((val, index) => {
          const option = savedOptions.find((o: Option) => getOptionValue(o) === val);

          if (!option) {
            return [];
          }

          return { ...normalizeOption(option), _key: index + '-' + getOptionValue(option) };
        });
      }

      if (!Array.isArray(value)) {
        return [];
      }

      return value
        .map((val) => savedOptions.find((option: Option) => getOptionValue(option) === val))
        .filter((option): option is Option => option !== undefined);
    }

    const result = savedOptions.find((option: Option) => getOptionValue(option) === value);

    return result !== undefined ? normalizeOption(result) : null;
  }, [value, isMulti, savedOptions, allowSelectSameOption]);

  const onChange: typeof onChangeProp = (value, newOption, actionMeta) => {
    const isEqual = _.isEqual(newOption, value);

    if (shouldTriggerOnEqualOptionChange || !isEqual) {
      onChangeProp(value, newOption, actionMeta);
    }
  };

  const handleChange = (newOption: any, actionMeta: ActionMeta<any>) => {
    if (closeMenuOnSelect && isCustomMenuTriggers) {
      setMenuIsOpen(false);
    }

    if (isMulti) {
      if (allowSelectSameOption) {
        if (actionMeta.action === 'deselect-option') {
          const isRemovable = isOptionRemovable
            ? isOptionRemovable(getValueFromReactSelectOption(actionMeta.option))
            : true;

          if (!isRemovable) {
            return;
          }

          const selectedValues = Array.isArray(selected) ? selected.map(getOptionValue) : [];
          const newSelectedValue = getOptionValue(getValueFromReactSelectOption(actionMeta.option));
          onChange([...selectedValues, newSelectedValue] as any, actionMeta.option, actionMeta);
          return;
        }

        if (actionMeta.action === 'remove-value') {
          const selectedValues = Array.isArray(selected) ? selected.map(getOptionValue) : [];
          const deletedValue = getOptionValue(getValueFromReactSelectOption(actionMeta.removedValue));

          const indexToDelete = selectedValues.lastIndexOf(deletedValue);

          if (indexToDelete !== -1) {
            selectedValues.splice(indexToDelete, 1);
          }

          onChange(selectedValues as any, actionMeta.removedValue, actionMeta);
          return;
        }
      } else {
        if (actionMeta.action === 'deselect-option') {
          const isRemovable = isOptionRemovable
            ? isOptionRemovable(getValueFromReactSelectOption(actionMeta.option))
            : true;

          if (!isRemovable) {
            return;
          }
        }
      }

      const newOptionValue = newOption.map(getValueFromReactSelectOption);
      onChange(newOptionValue.map(getOptionValue), newOptionValue, actionMeta);
      return;
    }

    const newOptionValue = getValueFromReactSelectOption(newOption);
    onChange(newOptionValue ? getOptionValue(newOptionValue) : newOptionValue, newOptionValue, actionMeta);
  };

  const normalizedOptions = useMemo(() => {
    const sortedOptions = sortFunction ? sortOptions(options || [], sortFunction) : [...(options || [])];

    if (emptyOption) {
      sortedOptions.unshift({ value: undefined, _isClearValue: true, label: emptyOption.label } as any);
    }

    const normalized = sortedOptions.map(normalizeOption);

    if (!isSearchMenuEnabled || filterOption === false) {
      return normalized;
    }

    if (!inputValue) {
      return normalized;
    }

    const defaultFilterFn = (option: any, searchValue: string) => {
      const optionLabel = getOptionLabel(option);
      const label =
        typeof optionLabel === 'string'
          ? optionLabel
          : formatOptionLabel?.(option, { context: 'menu', inputValue: searchValue, selectValue: [] as any });

      if (typeof label === 'string') {
        return label.toLowerCase().includes(searchValue.toLowerCase());
      }

      if (label && typeof label === 'object' && '$$typeof' in label) {
        // @ts-expect-error React 19 ReactElement type compatibility
        const text = extractTextFromReactNode(label);
        return text.toLowerCase().includes(searchValue.toLowerCase());
      }

      return true;
    };

    const filterFn = filterOption || defaultFilterFn;

    const filterSingleOption = (option: any) => filterFn(getOptionFromReactSelectOption(option), inputValue);

    const filterGroupOptions = (group: any) => {
      const filteredOptions = group.options.filter(filterSingleOption);
      return filteredOptions.length > 0 ? [{ ...group, options: filteredOptions }] : [];
    };

    return normalized.flatMap((option) =>
      isGroup(option) ? filterGroupOptions(option) : filterSingleOption(option) ? [option] : []
    );
  }, [
    options,
    isSearchMenuEnabled,
    // prevent unnecessary re-renders if we using default searching input
    isSearchMenuEnabled ? inputValue : null,
    isSearchMenuEnabled ? filterOption : null,
    sortFunction,
    emptyOption
  ]);

  const handleMenuOpen = () => {
    setMenuIsOpen(true);
    onMenuOpen?.();
  };

  const handleMenuClose = () => {
    setMenuIsOpen(false);
    onMenuClose?.();
  };

  const handleSearchChange = (newSearch: string) => {
    setInputValue(newSearch);
    onSearchChange?.(newSearch);
  };

  const renderPlaceholderAsValue = propsRenderPlaceholderAsValue || !!emptyOption?.enabled;

  const Component = useMemo(() => {
    if (creatable) {
      return CreatableSelect;
    }

    if (loadOptions) {
      return AsyncSelect;
    }

    return ReactSelect;
  }, [creatable, loadOptions]);

  const modifiedLoadOptions = useCallback(
    async (input: string): Promise<Option[]> => {
      if (!input.trim()) {
        return [];
      }

      if (input.trim().length < minSearchLengthToLoadOptions) {
        return [];
      }

      return new Promise((resolve, reject) => {
        if (loadOptionsTimeoutRef.current) {
          clearTimeout(loadOptionsTimeoutRef.current);
        }

        loadOptionsTimeoutRef.current = setTimeout(async () => {
          try {
            const loadedOptions = await loadOptions?.(input);

            setSavedOptions((current) => mergeSavedOptions(current, loadedOptions || []));

            resolve(loadedOptions || []);
          } catch (error) {
            reject(error);
          }
        }, loadOptionsDebounceDelay);
      });
    },
    [loadOptions, mergeSavedOptions, minSearchLengthToLoadOptions, loadOptionsDebounceDelay]
  );

  const handleNoOptionsMessage = useCallback(
    ({ inputValue: input }: { inputValue: string }) => {
      if (loadOptions && input.length < minSearchLengthToLoadOptions) {
        if (minSearchLengthToLoadOptions > 1) {
          return (
            <Trans i18nKey="quicksearch:select.minimum-search" values={{ count: minSearchLengthToLoadOptions }}>
              <i />
            </Trans>
          );
        }
        return null;
      }

      if (noOptionsMessage) {
        return noOptionsMessage({ inputValue: input });
      }

      return <Trans i18nKey="quicksearch:select.no-options-found" values={{ value: input }} />;
    },
    [minSearchLengthToLoadOptions]
  );

  return (
    <RioSelectProvider
      value={{
        ...props,
        selected,
        value,
        displaySearchInMenu,
        onMenuClose: handleMenuClose,
        onMenuOpen: handleMenuOpen,
        search: inputValue,
        onSearchChange: handleSearchChange,
        isSearchMenuEnabled,
        isCustomMenuTriggers,
        prefix,
        suffix
      }}
    >
      <Component
        instanceId={instanceId}
        ref={(node: SelectInstance | null) => {
          if (selectRefProp) {
            selectRefProp.current = node as any;
          }
          ref.current = node as any;
        }}
        onFocus={onFocus}
        onBlur={onBlur}
        isLoading={isLoading}
        closeMenuOnSelect={closeMenuOnSelect}
        isMulti={isMulti as any}
        isSearchable={displaySearchInMenu ? false : isSearchable}
        placeholder={emptyOption?.enabled ? emptyOption.label : placeholder}
        isDisabled={disabled}
        isClearable={isClearable}
        classNamePrefix="rio-select"
        className={cx('rio-select', theme, className, size, variant, `value-color-${valueColor}`, {
          'display-search-in-menu': isSearchMenuEnabled,
          'render-placeholder-as-value': renderPlaceholderAsValue,
          'hide-border-bottom': hideBorderBottom,
          'is-modified':
            formShowModifiedIndicator &&
            isClearable &&
            (isMulti ? Array.isArray(selected) && selected.length > 0 : selected != null)
        })}
        options={normalizedOptions}
        value={selected}
        hideSelectedOptions={hideSelectedOptions}
        formatOptionLabel={
          formatOptionLabel
            ? (option: any, meta: any) => {
                if (option._isClearValue) {
                  return emptyOption?.label;
                }

                return formatOptionLabel
                  ? formatOptionLabel(getValueFromReactSelectOption(option), meta)
                  : getOptionLabel(option._isNormalized ? option.value : option);
              }
            : undefined
        }
        getOptionLabel={(option: any) => {
          if (option.__isNew__) {
            return option.label;
          }

          if (option._isClearValue) {
            return emptyOption?.label;
          }

          return getOptionLabel(option._isNormalized ? option.value : option);
        }}
        getOptionValue={(option: any) =>
          getOptionValue(option._isNormalized || option.__isNew__ ? option.value : option)
        }
        onChange={handleChange}
        inputValue={isSearchMenuEnabled ? undefined : search}
        onInputChange={isSearchMenuEnabled ? undefined : onSearchChange}
        autoFocus={autoFocus}
        styles={styles as any}
        components={{
          MultiValueRemove,
          Group,
          Option,
          Menu,
          ValueContainer,
          NoOptionsMessage,
          ClearIndicator,
          Control,
          ...(hideDropdownIndicator ? { DropdownIndicator: null } : {}),
          ...(valuePrefix ? { ValueContainer } : {}),
          ...components
        }}
        blurInputOnSelect={false}
        isOptionDisabled={
          isOptionDisabled
            ? (option: any) => isOptionDisabled(getValueFromReactSelectOption(option) as Option)
            : undefined
        }
        noOptionsMessage={handleNoOptionsMessage}
        menuIsOpen={menuIsOpen}
        onMenuOpen={isCustomMenuTriggers ? undefined : handleMenuOpen}
        onMenuClose={isCustomMenuTriggers ? undefined : handleMenuClose}
        formatGroupLabel={formatGroupLabel ? (group: any) => formatGroupLabel(group as any) : undefined}
        menuPortalTarget={menuPortalTarget}
        menuPosition={menuPosition}
        aria-label={ariaLabel}
        filterOption={
          filterOption
            ? (option: any, filterOptionSearch: any) => filterOption(option.data, filterOptionSearch)
            : filterOption === false
              ? null
              : undefined
        }
        {...(loadOptions && { loadOptions: modifiedLoadOptions })}
        {...(cacheOptions && { cacheOptions })}
        {...(optionsLoadingMessage && { loadingMessage: optionsLoadingMessage })}
      />
    </RioSelectProvider>
  );
}

const NoOptionsMessage = (props: any) => {
  const { isSearchMenuEnabled, search } = useRioSelectContext();

  if (isSearchMenuEnabled) {
    return (
      <ReactSelectComponents.NoOptionsMessage {...props}>
        <span>
          <Trans i18nKey="quicksearch:select.no-options-found" values={{ value: search }} />
        </span>
      </ReactSelectComponents.NoOptionsMessage>
    );
  }

  return <ReactSelectComponents.NoOptionsMessage {...props} />;
};

const ValueContainer = (props: any) => {
  const {
    valuePrefix,
    maxTagsCount,
    maxTagsRender = (left) => `+${left}`,
    allowSelectSameOption
  } = useRioSelectContext();

  const value = props.getValue();

  const isValueEmpty = !value || (Array.isArray(value) && value.length === 0);

  const [firstChild, otherChildren] = props.children;

  let normalizedFirstChild = Array.isArray(firstChild) ? firstChild : [firstChild];

  // To allow selecting the same option multiple times, we need to update the key for each child.
  // This is necessary because react-select uses option.value as the key.
  if (allowSelectSameOption) {
    normalizedFirstChild = normalizedFirstChild.map((child) => {
      if (typeof child === 'object' && child !== null && child.props?.data?._key) {
        return <Fragment key={child.props.data._key}>{child}</Fragment>;
      }
      return child;
    });
  }

  if (!maxTagsCount) {
    return (
      <ReactSelectComponents.ValueContainer {...props}>
        {valuePrefix && !isValueEmpty && <div className="rio-select__value-prefix">{valuePrefix}</div>}
        {normalizedFirstChild}
        {otherChildren}
      </ReactSelectComponents.ValueContainer>
    );
  }

  const overflowCounter = Math.max(0, normalizedFirstChild.length - maxTagsCount);
  const displayChips = normalizedFirstChild.slice(0, maxTagsCount);

  return (
    <ReactSelectComponents.ValueContainer {...props}>
      <Stack
        spacingInRems={0}
        alignItems="center"
        style={{ maxWidth: '100%', display: isValueEmpty ? 'contents' : undefined }}
      >
        {valuePrefix && !isValueEmpty && <div className="rio-select__value-prefix">{valuePrefix}</div>}
        {displayChips}
        {overflowCounter > 0 && (
          <div className="rio-select__overflow-counter" style={{ flexShrink: 0 }}>
            {maxTagsRender(overflowCounter)}
          </div>
        )}
      </Stack>
      {otherChildren}
    </ReactSelectComponents.ValueContainer>
  );
};

const ClearIndicator = (props: any) => {
  const originalOnMouseDown = props.innerProps.onMouseDown;

  return (
    <ReactSelectComponents.ClearIndicator
      {...props}
      innerProps={{
        ...props.innerProps,
        onMouseDown: (e: any) => {
          originalOnMouseDown?.(e);
          window.dispatchEvent(new Event('clear-indicator-clicked'));
        }
      }}
    />
  );
};

const Group = (props: any) => {
  if (props.data.background) {
    return (
      <div
        style={{
          background: props.data.background
        }}
      >
        <ReactSelectComponents.Group {...props} />
      </div>
    );
  }

  return <ReactSelectComponents.Group {...props} />;
};

const MultiValueRemove = (props: any) => {
  const { isOptionRemovable } = useRioSelectContext();

  if (isOptionRemovable && !isOptionRemovable(props.data)) {
    return <div style={{ width: 4 }} />;
  }

  return (
    <ReactSelectComponents.MultiValueRemove
      {...props}
      innerProps={{
        ...props.innerProps,
        onClick: (e: any) => {
          e.stopPropagation();
          e.preventDefault();
          props.innerProps.onClick(e);
        }
      }}
    />
  );
};

const Option = (props: any) => {
  if (props.data.menuVisible === false) {
    return null;
  }

  return <ReactSelectComponents.Option {...props} />;
};

const Menu = (props: any) => {
  const { isMenuLoading, isSearchMenuEnabled, search, onSearchChange } = useRioSelectContext();
  const isTouchDevice = useIsTouchDevice();

  return (
    <ReactSelectComponents.Menu {...props}>
      {isSearchMenuEnabled && (
        <div className="slds-p-around--x-small">
          <FormControlInput
            id="rio-select-search"
            value={search}
            className="input-scale-mobile"
            onChange={(e) => onSearchChange?.(e.target.value)}
            placeholder={t('quicksearch:select.search-placeholder')}
            variant="rio"
            autoFocus={!isTouchDevice}
            onMouseDown={eventHandlerUtils.stopPropagation}
            onTouchEnd={eventHandlerUtils.stopPropagation}
            onKeyDown={eventHandlerUtils.stopPropagation}
          />
        </div>
      )}
      {isMenuLoading ? (
        <Stack className="slds-p-around--medium" alignItems="center" justifyContent="center">
          <InlineSpinner />
        </Stack>
      ) : (
        props.children
      )}
    </ReactSelectComponents.Menu>
  );
};

const Control = ({ children, ...props }: any) => {
  const { prefix, suffix } = useRioSelectContext();

  return (
    <ReactSelectComponents.Control {...props}>
      {prefix && <span className="rio-select__prefix-container">{prefix}</span>}
      {children}
      {suffix && <span className="rio-select__suffix-container">{suffix}</span>}
    </ReactSelectComponents.Control>
  );
};
